import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Stack,
    Alert,
    LinearProgress,
    Card,
    CardContent,
    Divider,
    CircularProgress,
} from '@mui/material';
import { IconMail, IconCalendar, IconSend, IconUsers, IconClock, IconMapPin } from '@tabler/icons';
import { useMutation, useQuery, gql } from '@apollo/client';
import { format } from 'date-fns';

// GraphQL Query to get invitation email template
const GET_INVITATION_TEMPLATE = gql`
    query GetInvitationTemplate($contextId: String!, $templateType: String!) {
        getEmailTemplate(contextId: $contextId, templateType: $templateType) {
            subject
            body
            isHtml
        }
    }
`;

// GraphQL Mutation for sending invitations
const SEND_INVITATIONS = gql`
    mutation SendInvitations($eventId: String!, $role: String!, $themeId: String!, $selectedIds: [String!]!) {
        sendInvitations(eventId: $eventId, role: $role, themeId: $themeId, selectedIds: $selectedIds) {
            success
            message
            successCount
            failedCount
            results {
                id
                email
                success
                error
            }
        }
    }
`;

// Send Invitation Component
const SendInvitation = ({ selectedIds = [], eventId, role, themeId, theme, onClose, onSuccess }) => {
    const [open, setOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);

    // Fetch invitation email template
    const { data: templateData, loading: templateLoading } = useQuery(GET_INVITATION_TEMPLATE, {
        variables: { contextId: eventId, templateType: 'invitation' },
        skip: !eventId,
    });

    // GraphQL mutation
    const [sendInvitations] = useMutation(SEND_INVITATIONS, {
        onCompleted: data => {
            setSending(false);
            setResult(data.sendInvitations);
            if (onSuccess) {
                onSuccess(data.sendInvitations);
            }
        },
        onError: error => {
            setSending(false);
            setResult({
                success: false,
                message: error.message,
                successCount: 0,
                failedCount: selectedIds.length,
            });
        },
    });

    // Handle send invitations
    const handleSendInvitations = async () => {
        setSending(true);
        setResult(null);

        try {
            await sendInvitations({
                variables: {
                    eventId,
                    role,
                    themeId,
                    selectedIds,
                },
            });
        } catch (error) {
            console.error('Error sending invitations:', error);
        }
    };

    // Handle dialog close
    const handleClose = () => {
        setOpen(false);
        setResult(null);
        if (onClose) {
            onClose();
        }
    };

    return (
        <>
            {/* Send Invitation Button */}
            <Button
                variant='contained'
                startIcon={<IconMail size={20} />}
                onClick={() => setOpen(true)}
                disabled={selectedIds.length === 0}
                sx={{
                    background: 'linear-gradient(45deg, #2196F3 30%, #1976D2 90%)',
                    '&:hover': {
                        background: 'linear-gradient(45deg, #1976D2 30%, #2196F3 90%)',
                    },
                }}
            >
                Send Invitation ({selectedIds.length})
            </Button>

            {/* Send Invitation Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconMail size={24} />
                        Send Workshop Invitations
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {/* Selected Count */}
                    <Alert severity='info' sx={{ mb: 3 }}>
                        <Typography variant='body2'>
                            <strong>{selectedIds.length}</strong> attendee{selectedIds.length !== 1 ? 's' : ''} selected
                            for invitation
                        </Typography>
                    </Alert>

                    {/* Theme Information */}
                    <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                        <CardContent>
                            <Typography
                                variant='h6'
                                gutterBottom
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <IconCalendar size={20} />
                                Theme Information
                            </Typography>
                            <Stack spacing={1}>
                                <Typography variant='body1'>
                                    <strong>Theme:</strong> {theme?.name}
                                </Typography>
                                <Typography variant='body1'>
                                    <strong>Description:</strong> {theme?.description}
                                </Typography>
                                {theme?.type && (
                                    <Typography variant='body1'>
                                        <strong>Type:</strong> {theme?.type}
                                    </Typography>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* Email Preview */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant='h6' gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconMail size={20} />
                            Invitation Email Preview
                        </Typography>

                        {templateLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size={24} />
                                <Typography variant='body2' sx={{ ml: 2 }}>
                                    Loading invitation email template...
                                </Typography>
                            </Box>
                        ) : templateData?.getEmailTemplate ? (
                            <Card sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Typography variant='subtitle2' gutterBottom>
                                    Subject: {templateData.getEmailTemplate.subject}
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Box
                                    sx={{
                                        maxHeight: 200,
                                        overflow: 'auto',
                                        '& img': { maxWidth: '100%', height: 'auto' },
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: templateData.getEmailTemplate.body || 'No template content available',
                                    }}
                                />
                            </Card>
                        ) : (
                            <Card sx={{ p: 2, bgcolor: 'warning.light' }}>
                                <Typography variant='body2' color='warning.dark'>
                                    ⚠️ No invitation email template found. Please setup invitation email template first.
                                </Typography>
                            </Card>
                        )}
                    </Box>

                    {/* Loading State */}
                    {sending && (
                        <Box sx={{ mb: 2 }}>
                            <LinearProgress />
                            <Typography variant='body2' sx={{ mt: 1, textAlign: 'center' }}>
                                Sending invitations...
                            </Typography>
                        </Box>
                    )}

                    {/* Results */}
                    {result && (
                        <Alert severity={result.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                            <Typography variant='body1' gutterBottom>
                                {result.message}
                            </Typography>
                            <Typography variant='body2'>
                                <strong>Success:</strong> {result.successCount} | <strong>Failed:</strong>{' '}
                                {result.failedCount}
                            </Typography>
                        </Alert>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} disabled={sending}>
                        {result ? 'Close' : 'Cancel'}
                    </Button>
                    <Button
                        onClick={handleSendInvitations}
                        variant='contained'
                        disabled={sending}
                        startIcon={<IconSend size={20} />}
                        sx={{
                            background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                            '&:hover': {
                                background: 'linear-gradient(45deg, #45a049 30%, #4CAF50 90%)',
                            },
                        }}
                    >
                        {sending ? 'Sending...' : 'Send Invitations'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SendInvitation;
